import { Store } from './state/store';
import { ApiService } from './services/api';
import { AuthService } from './services/auth';
import { ScrollManager } from './services/scroll-manager';
import { StreamingManager } from './services/streaming-manager';
import { ModalManager } from './ui/modal-manager';
import {
    SidebarComponent,
    MessageListComponent,
    MessageInputComponent,
    ModelSelectorComponent,
    AuthComponent,
    SignInComponent,
    SignUpComponent,
    UserMenuComponent,
    ReactionPickerComponent,
    SettingsComponent
} from './components/mod';
import { clearCache } from './rendering';

declare global {
    interface Window {
        chatAppInitialized: boolean;
        updateScrollPosition?: () => void;
    }
}

class ChatApp {
    private store: Store;
    private api: ApiService;
    private auth: AuthService;
    private scrollManager: ScrollManager | null = null;
    private streamingManager: StreamingManager | null = null;

    private sidebar: SidebarComponent | null = null;
    private messageList: MessageListComponent | null = null;
    private messageInput: MessageInputComponent | null = null;
    private modelSelector: ModelSelectorComponent | null = null;
    private userMenu: UserMenuComponent | null = null;

    private signInComponent: SignInComponent | null = null;
    private signUpComponent: SignUpComponent | null = null;
    private authComponent: AuthComponent | null = null;
    private reactionPicker: ReactionPickerComponent | null = null;
    private settingsComponent: SettingsComponent | null = null;

    private pendingUpdateContent: string | null = null;
    private pendingUpdateAssistantId: string | null = null;
    private rafScheduled: boolean = false;

    constructor() {
        this.store = new Store();
        this.api = new ApiService();
        this.auth = AuthService.getInstance();
        this.settingsComponent = new SettingsComponent(this.store, this.api);

        this.bindRouting();

        setTimeout(async () => {
            await this.init();
        }, 0);
    }

    async init(): Promise<void> {
        await this.auth.initialize();

        this.handleRoute();

        this.auth.onAuthStateChange(() => {
            this.handleRoute();
        });

        window.addEventListener('hashchange', () => this.handleRoute());
    }

    private bindRouting(): void {
        const appElement = document.getElementById('app');
        if (!appElement) return;

        const userMenuContainer = document.createElement('div');
        userMenuContainer.id = 'user-menu-container';
        appElement.appendChild(userMenuContainer);
    }

    private handleRoute(): void {
        const hash = window.location.hash.slice(1) || '/';

        this.hideAllComponents();

        if (hash === '/sign-in') {
            this.showSignIn();
        } else if (hash === '/sign-up') {
            this.showSignUp();
        } else {
            if (this.auth.isAuthenticated()) {
                this.showApp();
            } else {
                this.showAuthGuard();
            }
        }
    }

    private hideAllComponents(): void {
        if (this.signInComponent) {
            this.signInComponent.destroy();
            this.signInComponent = null;
        }
        if (this.signUpComponent) {
            this.signUpComponent.destroy();
            this.signUpComponent = null;
        }
        if (this.authComponent) {
            const authGuard = document.querySelector('.auth-guard');
            if (authGuard) authGuard.remove();
        }
        if (this.userMenu) {
            this.userMenu.destroy();
            this.userMenu = null;
        }
        document.getElementById('app')?.classList.add('hidden');
    }

    private showAuthGuard(): void {
        const authGuard = document.querySelector('.auth-guard');
        if (authGuard) return;

        this.authComponent = new AuthComponent(document.body);
        this.authComponent.init();
    }

    private showSignIn(): void {
        const container = document.querySelector('.chat-container') as HTMLElement;
        if (container) {
            container.innerHTML = '';
            this.signInComponent = new SignInComponent(container);
        } else {
            this.signInComponent = new SignInComponent(document.body);
        }
        this.signInComponent.render();
    }

    private showSignUp(): void {
        const container = document.querySelector('.chat-container') as HTMLElement;
        if (container) {
            container.innerHTML = '';
            this.signUpComponent = new SignUpComponent(container);
        } else {
            this.signUpComponent = new SignUpComponent(document.body);
        }
        this.signUpComponent.render();
    }

    private async showApp(): Promise<void> {
        return new Promise(async (resolve) => {
            const appElement = document.getElementById('app');
            if (!appElement) {
                resolve();
                return;
            }

            appElement.classList.remove('hidden');

            const authGuard = document.querySelector('.auth-guard');
            if (authGuard) authGuard.remove();

            const authPage = document.querySelector('.auth-page');
            if (authPage) authPage.remove();

            const app = document.getElementById('app');
            if (app && !app.querySelector('.app-container')) {
                this.render();
            }

            if (!this.scrollManager) {
                this.scrollManager = new ScrollManager(this.store);
            }

            if (!this.streamingManager) {
                this.streamingManager = new StreamingManager(this.store, this.api);
                this.streamingManager.setOnInit((assistantId) => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            this.scrollManager?.expandAndScrollToExchange(assistantId);
                        });
                    });
                });
                // WARNING: Do NOT remove the RAF throttling below!
                // Without this, every SSE token triggers a full markdown re-render (including
                // code highlighting and icon creation), which blocks the main thread. This causes
                // network tokens to buffer up, making streaming appear "chunky" instead of smooth.
                // RAF throttling limits renders to 60fps max, keeping the UI responsive.
                this.streamingManager.setOnUpdateMessage((assistantId, content) => {
                    this.pendingUpdateContent = content;
                    this.pendingUpdateAssistantId = assistantId;
                    
                    if (!this.rafScheduled) {
                        this.rafScheduled = true;
                        requestAnimationFrame(() => {
                            this.rafScheduled = false;
                            if (this.pendingUpdateContent !== null && this.pendingUpdateAssistantId !== null) {
                                this.messageList?.updateLastMessage(this.pendingUpdateContent, this.pendingUpdateAssistantId);
                                this.scrollManager?.updatePaddingDuringStreaming(this.pendingUpdateAssistantId);
                            }
                        });
                    }
                });
                this.streamingManager.setOnStreamingComplete(() => {
                    this.messageList?.removeCursor();
                    this.messageInput?.focus();
                    this.messageList?.showControlsOnLastMessage();
                });
                this.streamingManager.setOnUpdateActionSteps((assistantId, actionSteps) => {
                    this.messageList?.updateActionHistory(assistantId, actionSteps);
                });
            }

            if (!this.sidebar) {
                const sidebarEl = document.getElementById('sidebar');
                if (sidebarEl) {
                    this.sidebar = new SidebarComponent(sidebarEl, this.store);
                }
            }

            if (!this.messageList) {
                const messagesContainerEl = document.getElementById('messages-container');
                if (messagesContainerEl) {
                    this.messageList = new MessageListComponent(messagesContainerEl, this.store);
                }
            }

            if (!this.messageInput) {
                const inputAreaEl = document.querySelector('.input-area') as HTMLElement;
                if (inputAreaEl) {
                    this.messageInput = new MessageInputComponent(inputAreaEl, this.store);
                }
            }

            if (!this.modelSelector) {
                const modelSelectorEl = document.getElementById('model-selector-container');
                if (modelSelectorEl) {
                    this.modelSelector = new ModelSelectorComponent(modelSelectorEl, this.store);
                }
            }

            if (!this.userMenu) {
                const userMenuEl = document.getElementById('user-menu-container');
                if (userMenuEl) {
                    this.userMenu = new UserMenuComponent(userMenuEl);
                    this.userMenu.render();
                }
            }

            if (!this.reactionPicker) {
                const reactionPickerContainer = document.createElement('div');
                reactionPickerContainer.id = 'reaction-picker-container';
                document.body.appendChild(reactionPickerContainer);
                this.reactionPicker = new ReactionPickerComponent(reactionPickerContainer);
            }

            if (!window.chatAppInitialized) {
                this.bindAppEvents();
                window.chatAppInitialized = true;
            }

            await this.loadInitialData();
            resolve();
        });
    }

    private async loadInitialData(): Promise<void> {
        this.scrollManager?.setup();
        this.setupMobileSidebar();
        this.setupPullToRefresh();

        this.store.setUserScrolledUp(false);
        this.store.setAutoScroll(true);

        try {
            await this.loadConfig();
            await this.loadModels();
            await this.loadConversations();
            this.restoreSession();
        } catch (error) {
            console.error('Failed to load initial data:', error);
        }
    }

    private bindAppEvents() {
        requestAnimationFrame(() => {
            document.addEventListener('new-chat', () => this.handleNewChat());
            document.addEventListener('select-conversation', (e) => this.handleSelectConversation((e as CustomEvent).detail));
            document.addEventListener('delete-conversation', (e) => this.handleDeleteConversation((e as CustomEvent).detail));
            document.addEventListener('copy-message', (e) => this.handleCopyMessage((e as CustomEvent).detail));
            document.addEventListener('edit-message', (e) => this.handleEditMessage((e as CustomEvent).detail));
            document.addEventListener('try-again', (e) => this.handleTryAgain((e as CustomEvent).detail));
            document.addEventListener('show-lightbox', (e) => this.handleShowLightbox((e as CustomEvent).detail));
            document.addEventListener('update-conversation-model', (e) => this.handleUpdateConversationModel((e as CustomEvent).detail));
            document.addEventListener('add-reaction', (e) => this.handleAddReaction((e as CustomEvent).detail));
            document.addEventListener('open-settings', () => this.settingsComponent?.show());

            window.updateScrollPosition = () => this.scrollManager?.updateButtonPosition();

            const inputArea = document.getElementById('input-area');
            if (inputArea) {
                inputArea.addEventListener('send-message', () => this.handleSendMessage());
                inputArea.addEventListener('stop-message', () => this.handleStop());
                inputArea.addEventListener('cancel-edit', () => {
                    const editingMessageId = this.store.getEditingMessageId();
                    if (editingMessageId) {
                        this.handleCancelEdit(editingMessageId);
                    }
                });
            }

            const headerNewChat = document.getElementById('header-new-chat');
            if (headerNewChat) {
                headerNewChat.addEventListener('click', () => this.handleNewChat());
            }

            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    const isSidebarOpen = this.store.isSidebarOpenValue();
                    const editingMessageId = this.store.getEditingMessageId();
                    const pendingImages = this.store.getPendingImages();
                    
                    if (isSidebarOpen) {
                        this.closeSidebar();
                    } else if (editingMessageId) {
                        this.handleCancelEdit(editingMessageId);
                    } else if (pendingImages.length > 0) {
                        this.store.clearPendingImages();
                    }
                }
            });
            
            const focusInputIfNotStreaming = () => {
                const isMobile = window.matchMedia('(pointer: coarse)').matches;
                if (isMobile) return;
                
                const currentConversationId = this.store.getCurrentConversationId();
                if (currentConversationId) {
                    const streamingState = this.store.getStreamingState(currentConversationId);
                    if (!streamingState.isStreaming) {
                        this.messageInput?.focus();
                    }
                }
            };
            
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    focusInputIfNotStreaming();
                }
            });
            
            window.addEventListener('focus', focusInputIfNotStreaming);
        });
    }

    private async loadModels() {
        const models = await this.api.listModels();
        this.store.setModels(models);
    }

    private async loadConfig() {
        try {
            const config = await this.api.getConfig();
            this.store.setClientConfig(config);
        } catch (error) {
            console.error('Failed to load config:', error);
        }
    }

    private async loadConversations() {
        const conversations = await this.api.listConversations();
        this.store.setConversations(conversations);
    }

    private async handleNewChat() {
        const selectedModel = this.store.getSelectedModel();
        const newConversation = await this.api.createConversation(selectedModel);
        this.store.addConversation(newConversation);
        await this.handleSelectConversation(newConversation.id);
    }

    private async handleSelectConversation(conversationId: string) {
        const currentConversationId = this.store.getCurrentConversationId();
        if (currentConversationId) {
            this.scrollManager?.saveScrollPosition(currentConversationId);
        }

        const currentStreaming = this.store.getStreamingState(currentConversationId);
        if (currentStreaming.isStreaming && currentConversationId) {
            this.streamingManager?.saveStreamingMessages(currentConversationId, [...this.store.getMessages()]);
        }

        this.store.setCurrentConversation(conversationId);
        this.store.setEditingMessage(null);
        this.closeSidebar();

        const conversations = this.store.getConversations();
        const conversation = conversations.find(c => c.id === conversationId);
        if (conversation) {
            const models = this.store.getModels();
            const defaultModel = this.store.getDefaultModel();
            const isModelAvailable = models.some(m => m.id === conversation.model);
            const modelToUse = isModelAvailable ? conversation.model : defaultModel;
            if (!isModelAvailable && modelToUse) {
                await this.handleUpdateConversationModel({ conversationId, model: modelToUse });
            }
            this.store.setSelectedModel(modelToUse);
            localStorage.setItem('currentConversationId', conversationId);
        }

        clearCache();

        this.scrollManager?.clearStreamingPadding();

        if (this.streamingManager?.getStreamingMessages(conversationId)) {
            this.store.setMessages(this.streamingManager.getStreamingMessages(conversationId)!);
        }

        if (this.streamingManager?.hasStreamBuffer(conversationId)) {
            const buffer = this.streamingManager.getStreamBuffer(conversationId)!;
            this.store.updateMessage(buffer.assistantId, { content: buffer.content }, true);
            const streamingState = this.store.getStreamingState(conversationId);
            if (streamingState.isStreaming) {
                this.messageList?.updateLastMessage(buffer.content, buffer.assistantId);
            }
        } else if (!this.streamingManager?.getStreamingMessages(conversationId)) {
            this.store.setLoadingMessages(true);
            const messages = await this.api.getMessages(conversationId);
            this.store.setLoadingMessages(false);
            this.store.setMessages(messages);
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const messages = this.store.getMessages();
                if (messages.length > 0) {
                    const savedPosition = this.scrollManager?.getScrollPosition(conversationId);
                    if (savedPosition !== null && savedPosition !== undefined) {
                        this.scrollManager?.scrollTo(savedPosition, 'auto');
                    } else {
                        this.scrollManager?.scrollToBottom('auto');
                    }
                }
                this.scrollManager?.checkScrollContainerState();
                this.scrollManager?.updateButtonPosition();
                
                const streamingState = this.store.getStreamingState(conversationId);
                if (!streamingState.isStreaming) {
                    this.messageInput?.focus();
                }
            });
        });
    }

    private async handleDeleteConversation(conversationId: string) {
        await this.api.deleteConversation(conversationId);
        this.store.deleteConversation(conversationId);

        this.streamingManager?.cleanupConversation(conversationId);

        const conversations = this.store.getConversations();
        if (conversations.length > 0) {
            await this.handleSelectConversation(conversations[0].id);
        } else {
            await this.handleNewChat();
        }
    }

    private async handleSendMessage() {
        if (!this.messageInput || !this.messageList || !this.streamingManager) return;

        const currentConversationId = this.store.getCurrentConversationId();
        const selectedModel = this.store.getSelectedModel();
        const content = this.messageInput.getMessageContent();
        const imagesToSend = this.messageInput.getPendingImages();

        if (!currentConversationId || (!content && imagesToSend.length === 0)) return;

        const editingMessageId = this.store.getEditingMessageId();
        let images = imagesToSend;

        if (editingMessageId) {
            const messages = this.store.getMessages();
            const message = messages.find(m => m.id === editingMessageId);
            const messageIndex = messages.findIndex(m => m.id === editingMessageId);

            await this.api.deleteMessage(editingMessageId);

            if (messageIndex !== -1) {
                this.store.deleteMessagesAfter(messageIndex - 1, false);
            }

            this.store.setEditingMessage(null);

            if (message) {
                images = message.images || imagesToSend;
            }
        }

        this.messageInput.resetInput();

        this.store.startStreaming(currentConversationId, false);
        this.messageList.removeCursor();
        this.messageList.clearVisibleControls();

        await this.streamingManager.startStream(
            currentConversationId,
            content,
            selectedModel,
            images
        );
    }

    private async handleStop() {
        if (!this.messageList || !this.streamingManager) return;

        const currentConversationId = this.store.getCurrentConversationId();
        if (currentConversationId) {
            await this.streamingManager.stopStream(currentConversationId);
        }
        this.messageList.removeCursor();
    }

    private async handleTryAgain(messageId: string) {
        const currentConversationId = this.store.getCurrentConversationId();
        if (!currentConversationId || !this.streamingManager || !this.messageList) return;

        ModalManager.showTryAgainModal(
            async () => {
                await this.executeTryAgain(messageId);
            },
            async () => {
                const modelSelector = document.getElementById('model-select') as HTMLSelectElement;
                const trigger = document.getElementById('model-select-trigger');
                
                if (modelSelector) {
                    modelSelector.focus();
                    modelSelector.scrollIntoView({ block: 'center' });

                    const onSelect = async () => {
                        modelSelector.removeEventListener('change', onSelect);
                        const newModel = modelSelector.value;
                        await this.handleUpdateConversationModel({
                            conversationId: currentConversationId,
                            model: newModel,
                        });
                        await this.executeTryAgain(messageId, newModel);
                    };
                    modelSelector.addEventListener('change', onSelect);
                } else if (this.modelSelector) {
                    this.modelSelector.openDropdown();
                    this.modelSelector.setOneTimeSelectCallback(async (newModel: string) => {
                        await this.handleUpdateConversationModel({
                            conversationId: currentConversationId,
                            model: newModel,
                        });
                        await this.executeTryAgain(messageId, newModel);
                    });
                }
            }
        );
    }

    private async executeTryAgain(messageId: string, model?: string) {
        if (!this.messageList || !this.messageInput || !this.streamingManager) return;

        const currentConversationId = this.store.getCurrentConversationId();
        const messages = this.store.getMessages();
        const messageIndex = messages.findIndex(m => m.id === messageId);
        if (messageIndex === -1 || !currentConversationId) return;

        this.store.deleteMessagesAfter(messageIndex - 1, true);
        this.store.startStreaming(currentConversationId, true);
        this.messageList.removeCursor();
        this.messageList.clearVisibleControls();

        const result = await this.streamingManager.tryAgain(currentConversationId, messageId, model);
        
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.scrollManager?.expandAndScrollToExchange(result.assistantId);
            });
        });
    }

    private async handleUpdateConversationModel(detail: { conversationId: string; model: string }) {
        await this.api.updateConversationModel(detail.conversationId, detail.model);
        this.store.updateConversationModel(detail.conversationId, detail.model);
    }

    private async handleEditMessage(messageId: string) {
        const messages = this.store.getMessages();
        const message = messages.find(m => m.id === messageId);
        if (!message || message.role !== 'user') return;

        this.store.setEditingMessage(messageId);
        this.messageInput?.setEditingContent(message.content, message.images);
        this.messageInput?.focus();
    }

    private handleCancelEdit(messageId: string) {
        this.store.setEditingMessage(null);
        this.messageInput?.resetInput();
        this.messageInput?.focus();
    }

    private handleCopyMessage(messageId: string) {
        const messages = this.store.getMessages();
        const message = messages.find(m => m.id === messageId);
        if (!message) return;

        navigator.clipboard.writeText(message.content);
        const btn = document.querySelector(`.message-copy-btn[data-id="${messageId}"]`) as HTMLElement;
        if (btn) {
            const originalTooltip = btn.getAttribute('data-tooltip');
            btn.setAttribute('data-tooltip', 'Copied!');
            btn.innerHTML = '<i data-lucide="check-square"></i>';
            btn.classList.add('message-copied');
            (window as any).lucide?.createIcons();
            setTimeout(() => {
                btn.setAttribute('data-tooltip', originalTooltip || 'Copy');
                btn.innerHTML = '<i data-lucide="copy"></i>';
                btn.classList.remove('message-copied');
                (window as any).lucide?.createIcons();
            }, 2000);
        }
    }

    private handleShowLightbox(detail: { imageIndex: number; totalImagesInMessage: number }) {
        const allImages = this.messageList?.getAllImages();
        if (!allImages || allImages.length === 0) return;

        ModalManager.showLightbox(allImages, detail.imageIndex);
    }

    private async handleAddReaction(detail: { messageId: string; emoji: string; x: number; y: number }) {
        if (!this.reactionPicker) return;
        
        this.reactionPicker.show(detail.messageId, detail.x, detail.y, async (emoji: string) => {
            const messages = this.store.getMessages();
            const message = messages.find(m => m.id === detail.messageId);
            if (!message) return;
            
            const existingReaction = message.reactions?.find(r => r.from_role === 'user');
            
            if (existingReaction && existingReaction.emoji === emoji) {
                this.store.removeReaction(detail.messageId, 'user');
                await this.api.updateMessageMetadata(detail.messageId, {
                    reactions: message.reactions?.filter(r => r.from_role !== 'user') || []
                });
                return;
            }
            
            let content: string;
            const shortId = message.id.replace(/-/g, '').substring(0, 8);
            if (message.role === 'assistant') {
                const snippetWords = this.store.getReactionSnippetWords();
                const snippet = this.createContentSnippet(message.content, snippetWords);
                content = `[REACTION] ${emoji} to assistant (msg_${shortId}): "${snippet}"`;
            } else {
                content = `[REACTION] ${emoji} to msg_${shortId}`;
            }
            
            const currentModel = this.store.getSelectedModel();
            const conversationId = this.store.getCurrentConversationId();
            
            if (conversationId) {
                const updatedReactions = [
                    ...(message.reactions?.filter(r => r.from_role !== 'user') || []),
                    { emoji, from_role: 'user' as const }
                ];
                this.store.addReaction(detail.messageId, emoji, 'user');
                await this.api.updateMessageMetadata(detail.messageId, { reactions: updatedReactions });
                
                this.streamingManager?.startStream(conversationId, content, currentModel, []);
            }
        });
    }

    private createContentSnippet(content: string, wordCount: number): string {
        const words = content.trim().split(/\s+/);
        if (words.length <= wordCount) {
            return content.trim();
        }
        return words.slice(0, wordCount).join(' ') + '...';
    }

    private async restoreSession() {
        const savedConversationId = localStorage.getItem('currentConversationId');
        const conversations = this.store.getConversations();
        if (savedConversationId && conversations.find(c => c.id === savedConversationId)) {
            await this.handleSelectConversation(savedConversationId);
            this.tryResumeStream(savedConversationId);
        } else if (conversations.length > 0) {
            await this.handleSelectConversation(conversations[0].id);
            this.tryResumeStream(conversations[0].id);
        } else {
            this.handleNewChat();
        }
    }

    private async tryResumeStream(conversationId: string) {
        if (!this.streamingManager) return;
        
        const resumed = await this.streamingManager.resumeStream(conversationId);
        if (resumed) {
            console.log('Resumed active stream for conversation:', conversationId);
        }
    }

    private setupMobileSidebar() {
        const mobileMenuBtn = document.getElementById('mobile-menu-btn');
        const sidebarOverlay = document.getElementById('sidebar-overlay');

        const checkScreenSize = () => {
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
                this.store.setSidebarOpen(false);
                document.body.classList.add('sidebar-closed');
            } else {
                this.store.setSidebarOpen(true);
                document.body.classList.remove('sidebar-closed');
            }
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        mobileMenuBtn?.addEventListener('click', () => this.toggleSidebar());
        sidebarOverlay?.addEventListener('click', () => this.closeSidebar());
    }

    private toggleSidebar() {
        const isOpen = this.store.isSidebarOpenValue();
        this.store.setSidebarOpen(!isOpen);

        const sidebar = document.getElementById('sidebar');
        const sidebarOverlay = document.getElementById('sidebar-overlay');
        const mobileMenuBtn = document.getElementById('mobile-menu-btn');

        const isMobile = window.innerWidth < 768;

        if (!isOpen) {
            sidebar?.classList.add('open');
            if (isMobile) {
                sidebarOverlay?.classList.add('show');
                mobileMenuBtn?.setAttribute('aria-expanded', 'true');
                document.body.style.overflow = 'hidden';
            }
        } else {
            sidebar?.classList.remove('open');
            if (isMobile) {
                sidebarOverlay?.classList.remove('show');
                mobileMenuBtn?.setAttribute('aria-expanded', 'false');
                document.body.style.overflow = '';
            }
        }
    }

    private closeSidebar() {
        this.store.setSidebarOpen(false);

        const sidebar = document.getElementById('sidebar');
        const sidebarOverlay = document.getElementById('sidebar-overlay');
        const mobileMenuBtn = document.getElementById('mobile-menu-btn');

        const isMobile = window.innerWidth < 768;

        sidebar?.classList.remove('open');
        if (isMobile) {
            sidebarOverlay?.classList.remove('show');
            mobileMenuBtn?.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
        }
    }

    private setupPullToRefresh() {
        let startY = 0;
        let currentY = 0;
        let isPulling = false;
        let pullDistance = 0;
        const TRIGGER_THRESHOLD = 100;
        const MAX_PULL = 150;

        const messagesContainer = document.getElementById('messages-container');
        const main = document.querySelector('.main');
        const header = document.querySelector('.chat-header');
        const inputArea = document.querySelector('.input-area');

        if (!messagesContainer || !main) return;

        const createRefreshIndicator = () => {
            const container = document.createElement('div');
            container.className = 'pull-to-refresh-container';
            container.innerHTML = `
                <div class="pull-to-refresh-content">
                    <div class="refresh-icon">
                        <svg viewBox="0 0 24 24" width="40" height="40" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
                            <path class="arrow-path" d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                            <path class="arrow-path" d="M3 3v5h5"></path>
                            <path class="arrow-path" d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
                            <path class="arrow-path" d="M16 16h5v5"></path>
                        </svg>
                    </div>
                </div>
            `;
            return container;
        };

        const indicator = createRefreshIndicator();
        const refreshIcon = indicator.querySelector('.refresh-icon') as HTMLElement;

        indicator.style.position = 'fixed';
        indicator.style.bottom = '0';
        indicator.style.left = '0';
        indicator.style.right = '0';
        indicator.style.height = '60px';
        indicator.style.overflow = 'hidden';
        indicator.style.zIndex = '9999';
        indicator.style.pointerEvents = 'none';
        indicator.style.transform = 'translateY(60px)';
        indicator.style.opacity = '0';

        main.style.position = 'relative';
        main.insertAdjacentElement('beforeend', indicator);

        const isScrolledToBottom = () => {
            const scrollPosition = window.scrollY + window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            return documentHeight - scrollPosition < 10;
        };

        const onTouchStart = (e: TouchEvent) => {
            const isMobile = window.innerWidth < 768;
            if (!isMobile) return;

            if (isScrolledToBottom()) {
                startY = e.touches[0].clientY;
                isPulling = true;
                pullDistance = 0;
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!isPulling) return;

            currentY = e.touches[0].clientY;
            const diff = startY - currentY;

            if (diff > 0) {
                e.preventDefault();

                const easedDistance = Math.min(diff * 0.5, MAX_PULL);
                const progress = Math.min(easedDistance / TRIGGER_THRESHOLD, 1);

                pullDistance = easedDistance;

                const contentHeight = 60;
                const translateY = contentHeight - easedDistance;

                indicator.style.transform = `translateY(${translateY}px)`;
                indicator.style.opacity = progress.toString();

                if (header) {
                    header.style.transform = `translateY(${-easedDistance}px)`;
                }
                messagesContainer.style.transform = `translateY(${-easedDistance}px)`;
                if (inputArea) {
                    inputArea.style.transform = `translateY(${-easedDistance}px)`;
                }

                const rotation = Math.min(progress * 250, 200);
                if (refreshIcon) {
                    refreshIcon.style.transform = `rotate(${rotation}deg)`;
                }
            }
        };

        const onTouchEnd = () => {
            if (!isPulling) return;
            isPulling = false;

            if (pullDistance >= TRIGGER_THRESHOLD) {
                if (refreshIcon) {
                    refreshIcon.classList.add('spinning');
                }

                setTimeout(() => {
                    location.reload();
                }, 600);
            } else {
                indicator.style.transform = 'translateY(60px)';
                indicator.style.opacity = '0';

                if (header) {
                    header.style.transform = '';
                }
                messagesContainer.style.transform = '';
                if (inputArea) {
                    inputArea.style.transform = '';
                }
            }

            pullDistance = 0;
        };

        const mainContent = document.querySelector('.main');
        if (mainContent) {
            mainContent.addEventListener('touchstart', onTouchStart, { passive: true });
            mainContent.addEventListener('touchmove', onTouchMove, { passive: false });
            mainContent.addEventListener('touchend', onTouchEnd);
            mainContent.addEventListener('touchcancel', () => {
                isPulling = false;
                indicator.style.transform = 'translateY(60px)';
                indicator.style.opacity = '0';

                if (header) {
                    header.style.transform = '';
                }
                messagesContainer.style.transform = '';
                if (inputArea) {
                    inputArea.style.transform = '';
                }

                pullDistance = 0;
            });
        }
    }

    private render() {
        const app = document.getElementById('app');
        if (!app) {
            console.error('[render] App container not found!');
            throw new Error('App container not found');
        }

        console.log('[render] Rendering app HTML');

        app.innerHTML = `
            <div class="app-container">
                <aside class="sidebar" id="sidebar" aria-label="Conversations sidebar"></aside>
                <main class="main">
                    <header class="chat-header">
                        <div class="header-left">
                            <button class="mobile-menu-btn" id="mobile-menu-btn" aria-label="Open menu" aria-controls="sidebar" aria-expanded="false">
                                <i data-lucide="panel-left"></i>
                            </button>
                            <div id="model-selector-container"></div>
                        </div>
                        <div class="header-center">
                        </div>
                        <div class="header-right">
                            <button class="header-new-chat" id="header-new-chat" aria-label="Create new chat"><i data-lucide="plus"></i></button>
                        </div>
                    </header>
                    <div class="messages-container" id="messages-container">
                        <div class="messages-list" id="messages-list"></div>
                    </div>
                    <button id="scroll-to-bottom" class="scroll-to-bottom-btn hidden" aria-label="Scroll to bottom"><i data-lucide="arrow-down"></i></button>
                    <div class="input-area-wrapper">
                        <div class="input-area" id="input-area"></div>
                    </div>
                </main>
            </div>
            <div class="sidebar-overlay" id="sidebar-overlay" aria-hidden="true"></div>
        `;

        (window as any).lucide?.createIcons();
        
        setTimeout(() => this.scrollManager?.updateButtonPosition(), 0);
    }
}

(window as any).chatAppInitialized = false;

new ChatApp();